import type { TestShipmentCandidateRow } from '@/apis/logiblocksAPI';
import { formatUserDateTime } from '@/common/utils/userDateFormat';
import { WorkflowTestRunData } from '@/proto/logiblocks/logiblocks';
import { getSubgraphRoot } from '../graphUtils';
import { requireLBNodeInfo } from '../nodes/registry';
import { reactFlowToLBWorkflow } from '../serialization';
import type { ReactFlowEdge, ReactFlowNode } from '../types';
import { supportsTestRuns } from '../variables/variableDefinitions';

/** Returns the data a test run of the canvas graph runs with. */
export function buildTestRunData(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[],
  shipmentId: string,
  alertId: string,
  emailRecipients: string[],
  skipDelays: boolean
): WorkflowTestRunData {
  const trimmedEmailRecipients = emailRecipients
    .map((email) => email.trim())
    .filter((email) => email !== '');
  return WorkflowTestRunData.fromPartial({
    workflow: reactFlowToLBWorkflow(nodes, edges),
    shipment_id: shipmentId.trim(),
    alert_id: alertId.trim(),
    email_recipients: trimmedEmailRecipients,
    skip_delays: skipDelays,
  });
}

/**
 * Returns the alert types of the root graph's trigger, or null when that trigger isn't an alert
 * trigger. An empty list means the trigger passes every alert type.
 */
export function getAlertTriggerAlertTypes(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): string[] | null {
  const alertTrigger = getSubgraphRoot(undefined, nodes, edges)?.data
    .alert_trigger;
  if (alertTrigger === undefined) {
    return null;
  }
  // `alert_types` holds only the types that existed when "select all" was picked.
  if (alertTrigger.select_all_alert_types) {
    return [];
  }
  return alertTrigger.alert_types;
}

/**
 * Returns the id the shipment list shows for a test shipment: its reference number, plus
 * `-{link_code}` when it has one. A shipment with no reference number shows its shipment id.
 */
export function formatTestShipmentDisplayId(
  row: TestShipmentCandidateRow
): string {
  const referenceNumber = row.reference_number ?? '';
  if (referenceNumber === '') {
    return row.shipment_id;
  }
  const linkCode = row.link_code ?? '';
  if (linkCode === '') {
    return referenceNumber;
  }
  return `${referenceNumber}-${linkCode}`;
}

/**
 * Returns a test shipment's summary line, "date • courier • sensor • origin → destination", in the
 * user's `dateFormat`, leaving out the parts the shipment doesn't have. The date is the actual
 * pickup date, else the scheduled one, and the sensor is its manufacturer, else its name.
 */
export function formatTestShipmentMetaLine(
  row: TestShipmentCandidateRow,
  dateFormat: string | null | undefined
): string {
  const pickupDate = row.actual_pickup_date ?? row.scheduled_pickup_date;
  const dateText =
    pickupDate === null
      ? ''
      : formatUserDateTime(pickupDate, dateFormat, { includeTime: false });
  const sensorText = row.sensor_manufacturer_name || row.sensor_name || '';
  const routeText = [row.origin_name ?? '', row.destination_name ?? '']
    .filter((name) => name !== '')
    .join(' → ');
  return [dateText, row.courier_name, sensorText, routeText]
    .filter((part) => part !== '')
    .join(' • ');
}

/**
 * Returns the label of each node type on the canvas that test runs don't support, once per type,
 * in canvas order.
 */
export function getTestRunUnsupportedNodeLabels(
  nodes: ReactFlowNode[]
): string[] {
  const labels: string[] = [];
  for (const node of nodes) {
    const nodeInfo = requireLBNodeInfo(node.data);
    if (
      !supportsTestRuns(nodeInfo.protobufNodeDataKey) &&
      !labels.includes(nodeInfo.label)
    ) {
      labels.push(nodeInfo.label);
    }
  }
  return labels;
}
